'use client';
import { matchSorter } from 'match-sorter';
import { Combobox as ArkCombobox, createListCollection } from '@ark-ui/react';
import { Box, Card, Flex, Grid, GridItem, Icon, Input, ListCollection, Portal, Spinner, Stack, chakra } from '@chakra-ui/react';
import { ReactNode, useMemo, useState } from 'react';
import { InputGroup } from './input-group';
import { ChevronDown, X } from 'lucide-react';

interface Item {
    value: string;
    label: string;
}

const ComboboxRoot = chakra(ArkCombobox.Root, {
    base: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1',
    },
});
const ComboboxControl = chakra(ArkCombobox.Control, {});
const ComboboxInput = chakra(
    ArkCombobox.Input,
    {
        base: {
            bg: 'rgba(0, 0, 0, 0.02)',
            outlineColor: '#D6D6D6',
            borderColor: '#D6D6D6',
            _focusVisible: { outlineColor: '#D6D6D6', borderColor: '#D6D6D6' },
            _placeholder: { color: '#213547' },
        },
    },
    { forwardAsChild: true },
);

const ComboboxTrigger = chakra(ArkCombobox.Trigger, {}, { forwardAsChild: true });
const ComboboxContent = chakra(ArkCombobox.Content, {
    base: {
        borderRadius: 'md',
    },
});
const ComboboxItem = chakra(ArkCombobox.Item, {
    base: {
        bg: { base: 'white', _dark: 'purple.800' },
        _hover: {
            bg: 'gray.subtle',
            cursor: 'pointer',
        },
        _selected: {
            bg: 'gray.subtle',
        },
    },
});

const ComboboxPositioner = chakra(ArkCombobox.Positioner);

interface ComboboxProps<T extends Item> {
    items: T[];
    filterItems?: (value: string) => void;
    onChange: (value: string | undefined) => void;
    renderItem: (item: T) => ReactNode;
    renderSelection?: (item: T) => ReactNode;
    onValueInput?: (value: string) => void;
    name: string;
    value: T | undefined;
    loading?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    clearable?: boolean;
    autoFocus?: boolean;
    creatable?: boolean;
    defaultOpen?: boolean;
    portalled?: boolean;
}

const ComboboxClearTrigger = chakra(ArkCombobox.ClearTrigger, {}, { forwardAsChild: true });

export const Combobox = <T extends Item>({ clearable = true, portalled = true, ...props }: ComboboxProps<T>) => {
    const {
        filterItems,
        items,
        onChange,
        name,
        value,
        renderItem,
        renderSelection = renderItem,
        onValueInput,
        loading,
        disabled,
        autoFocus = false,
        defaultOpen = false,
        creatable = false,
        readOnly,
    } = props;
    const [inputValue, setInputValue] = useState<string>('');

    const collection = useMemo(() => {
        if (!filterItems) {
            const filteredItems = matchSorter(items, inputValue || '', { keys: ['label'] });
            return createListCollection<unknown>({ items: filteredItems });
        }
        return createListCollection<unknown>({ items: items });
    }, [items, inputValue, filterItems]);

    const isClearable = clearable && ((creatable && inputValue) || !!value);

    return (
        <ComboboxRoot
            flex={1}
            w="100%"
            name={name}
            collection={collection}
            disabled={disabled}
            readOnly={readOnly}
            inputValue={inputValue}
            autoFocus={autoFocus}
            defaultOpen={defaultOpen}
            openOnClick
            onValueChange={(e) => {
                const [value] = e.value;
                onChange(value);
                setInputValue('');
            }}
            onInput={(e) => {
                const value = (e.target as HTMLInputElement).value;
                filterItems?.(value);
                setInputValue(value);
                onValueInput?.(value);
            }}>
            <ComboboxControl>
                <Grid>
                    <GridItem gridArea="1/1/2/3">
                        <InputGroup
                            w="100%"
                            endElement={
                                <Flex gap="8px" align="center">
                                    <ComboboxClearTrigger
                                        asChild
                                        hidden={!isClearable}
                                        onClick={
                                            creatable && inputValue
                                                ? () => {
                                                      setInputValue('');
                                                      onValueInput?.('');
                                                  }
                                                : undefined
                                        }>
                                        <Icon boxSize={4} color="#52525B" cursor="pointer" aria-label="Clear selection">
                                            <X />
                                        </Icon>
                                    </ComboboxClearTrigger>

                                    {loading && <Spinner size="sm" />}
                                    <ComboboxTrigger asChild>
                                        <Icon boxSize={4} color="#52525B">
                                            <ChevronDown />
                                        </Icon>
                                    </ComboboxTrigger>
                                </Flex>
                            }>
                            <ComboboxInput asChild aria-describedby={value && !inputValue ? `${name}-selected-value` : undefined}>
                                <Input placeholder="" data-1p-ignore />
                            </ComboboxInput>
                        </InputGroup>
                    </GridItem>
                    {value && !inputValue && (
                        <GridItem
                            pl="12px"
                            h="36px"
                            gridArea="1/1/2/3"
                            w="calc(100% - 36px)"
                            id={`${name}-selected-value`}
                            role="status"
                            aria-live="polite">
                            <Flex gap="0" h="36px" alignItems="center" w="100%">
                                {renderSelection(value)}
                            </Flex>
                        </GridItem>
                    )}
                </Grid>
            </ComboboxControl>
            <Portal disabled={!portalled}>
                <ComboboxPositioner css={{ '--z-index': '2999 !important' }}>
                    {(collection.items.length || loading || (!collection.items.length && !creatable)) && (
                        <ComboboxContent
                            boxShadow="none"
                            px="0"
                            py="0"
                            overflow="auto"
                            maxH="50vh"
                            fontSize="14px"
                            border="1px solid #E4E4E7"
                            overscrollBehavior="contain">
                            <Card.Root variant="elevated">
                                <Card.Body p={0}>
                                    {collection.items.length ? (
                                        (collection as unknown as ListCollection<T>).items.map((item) => (
                                            <ComboboxItem key={item.value} item={item} persistFocus height="auto" px="2" py="2">
                                                <Stack gap="0">{renderItem(item)}</Stack>
                                            </ComboboxItem>
                                        ))
                                    ) : loading ? (
                                        <Box px="2" py="2">
                                            Loading Items
                                        </Box>
                                    ) : (
                                        <Box px="2" py="2">
                                            No results
                                        </Box>
                                    )}
                                </Card.Body>
                            </Card.Root>
                        </ComboboxContent>
                    )}
                </ComboboxPositioner>
            </Portal>
        </ComboboxRoot>
    );
};
